import { useCallback, useEffect, useRef } from "react";
import clsx from "clsx";
import { useDraggable } from "@dnd-kit/core";
import type { DayBlockSegment, POI, Tag } from "@poi-planner/shared";
import type { ScheduledTripNote } from "@poi-planner/shared";
import { useTripStore } from "../../store/tripStore";
import { useUIStore } from "../../store/uiStore";
import { Button } from "../Button";
import { TagIcon } from "../TagIcon";
import { eventDisplayTimeInfo, minutesBetween } from "../../utils/zonedTime";
import { requiresCrossZoneGestureConfirmation } from "../../utils/calendarGestures";
import { dndIds } from "../../utils/dndIds";
import type { BlockGeometry } from "../../utils/timeGrid";
import type { BlockLayout } from "../../utils/calendarLayout";
import type { BoardDragData } from "./dragTypes";
import { useBlockResize } from "./useBlockResize";
import { ResizeHandles } from "./ResizeHandles";
import { useLockedDragProbe } from "./useLockedDragProbe";
import { CalendarActionToolbar } from "./CalendarActionToolbar";
import { useCalendarToolbarHover } from "./useCalendarToolbarHover";
import { useCalendarDndStore } from "../../store/calendarDndStore";

interface NoteBlockProps {
  note: ScheduledTripNote;
  tags: Tag[];
  startPoi: POI | undefined;
  endPoi: POI | undefined;
  geometry: BlockGeometry;
  layout: BlockLayout;
  date: string;
  zone: string;
  isReadOnly: boolean;
  segment: DayBlockSegment;
  /** Selects this note's resolved location in the trip sidebar and visible map. */
  onShowOnMap?: () => void;
}

/**
 * A scheduled trip note on the time grid — dark treatment per the mockup
 * (transport is a note tagged with a mode). Draggable and resizable like POI
 * blocks; click (or the edit action) opens the NoteEditorModal.
 */
export function NoteBlock({
  note,
  tags,
  startPoi,
  endPoi,
  geometry,
  layout,
  date,
  zone,
  isReadOnly,
  segment,
  onShowOnMap,
}: NoteBlockProps) {
  const {
    hovered,
    onMouseEnter: handleToolbarMouseEnter,
    onMouseLeave: handleToolbarMouseLeave,
  } = useCalendarToolbarHover();
  const blockRef = useRef<HTMLDivElement>(null);
  const wasDragging = useRef(false);

  const updateNote = useTripStore((s) => s.updateNote);
  const deleteNote = useTripStore((s) => s.deleteNote);
  const openNoteEditor = useUIStore((s) => s.openNoteEditor);
  const setResizePreview = useCalendarDndStore((s) => s.setResizePreview);

  const locked = note.locked;
  const spanMinutes = Math.max(
    Math.round(minutesBetween(note.startTime, note.endTime)),
    0,
  );

  const {
    preview: resizePreview,
    resizing,
    handleProps,
  } = useBlockResize({
    startTime: note.startTime,
    endTime: note.endTime,
    date,
    zone,
    disabled: locked || isReadOnly,
    onCommit: (times, context) => {
      if (
        requiresCrossZoneGestureConfirmation(
          context.originalDisplayZone,
          context.targetDisplayZone,
        )
      ) {
        openNoteEditor("edit", note.id, undefined, {
          eventType: "note",
          entityId: note.id,
          gesture: "resize",
          ...times,
          originalDisplayZone: context.originalDisplayZone,
          targetDisplayZone: context.targetDisplayZone,
        });
        return;
      }
      updateNote(note.id, times);
    },
    onPreviewChange: setResizePreview,
  });

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dndIds.calendarNoteSegment(note.id, date),
    data: {
      type: "note",
      noteId: note.id,
      startTime: note.startTime,
      originDisplayDate: date,
      originDisplayZone: zone,
      spanMinutes,
      label: note.name,
    } satisfies BoardDragData,
    disabled: locked || isReadOnly || resizing || !segment.isStart,
  });

  const setBlockNode = useCallback(
    (node: HTMLDivElement | null) => {
      blockRef.current = node;
      setNodeRef(node);
    },
    [setNodeRef],
  );

  const lockedProbeProps = useLockedDragProbe(
    locked && !isReadOnly,
    "Unlock this note before changing its day or time.",
  );

  useEffect(() => {
    if (isDragging) wasDragging.current = true;
  }, [isDragging]);

  const displayStart = resizePreview?.startTime ?? note.startTime;
  const displayEnd = resizePreview?.endTime ?? note.endTime;
  const timeInfo = eventDisplayTimeInfo(displayStart, displayEnd, zone, {
    clippedTop: geometry.clippedTop,
    clippedBottom: geometry.clippedBottom,
  });

  const top = resizePreview?.top ?? geometry.top;
  const height = resizePreview?.height ?? geometry.height;
  const compact = height < 40;

  const primaryTag = tags[0];
  const routeSubtitle =
    startPoi || endPoi
      ? `${startPoi?.name ?? ""} → ${endPoi?.name ?? ""}`.trim()
      : null;

  const highlighted = hovered || resizing;
  const showActions = !isReadOnly && !resizing && hovered;
  const showHandles = !isReadOnly && !locked && highlighted;

  // Click opens the editor — the modal is also the tap affordance on touch
  const handleClick = () => {
    if (wasDragging.current) {
      wasDragging.current = false;
      return;
    }
    if (!isReadOnly) openNoteEditor("edit", note.id);
  };

  return (
    <div
      ref={setBlockNode}
      {...attributes}
      {...listeners}
      {...lockedProbeProps}
      onClick={handleClick}
      className={clsx(
        "absolute flex flex-col gap-px rounded-lg px-2 pt-1 pb-1 text-slate-200 shadow-sm",
        "border-none transition-colors",
        highlighted ? "bg-slate-700" : "bg-slate-800",
        locked || !segment.isStart ? "cursor-pointer" : "cursor-grab",
        showActions || showHandles ? "overflow-visible" : "overflow-hidden",
        isDragging && "opacity-40",
        resizing && "select-none",
      )}
      style={{
        top,
        height,
        left: `calc(8px + ${layout.leftPct}%)`,
        right: 8,
        borderLeft: "4px solid var(--color-sky-500)",
        zIndex: layout.zIndex,
      }}
      onMouseEnter={handleToolbarMouseEnter}
      onMouseLeave={handleToolbarMouseLeave}
      data-note-id={note.id}
      title={timeInfo.tooltip}
    >
      {showHandles && (
        <ResizeHandles
          handleProps={handleProps}
          showStart={segment.isStart}
          showEnd={segment.isEnd}
        />
      )}

      {showActions && (
        <CalendarActionToolbar
          anchorRef={blockRef}
          ariaLabel={`Actions for note ${note.name}`}
          onMouseEnter={handleToolbarMouseEnter}
          onMouseLeave={handleToolbarMouseLeave}
        >
          {onShowOnMap && (
            <Button
              variant="icon"
              size="sm"
              icon="location_searching"
              aria-label={`Show ${note.name} on map`}
              title="Show on map"
              onClick={onShowOnMap}
            />
          )}
          <Button
            variant="icon"
            size="sm"
            icon="edit"
            aria-label={`Edit note ${note.name}`}
            title="Edit note"
            onClick={() => openNoteEditor("edit", note.id)}
          />
          <Button
            variant="icon"
            size="sm"
            icon={locked ? "lock_open" : "lock"}
            aria-label={locked ? "Unlock time" : "Lock time"}
            title={locked ? "Unlock time" : "Lock time"}
            onClick={() => updateNote(note.id, { locked: !locked })}
          />
          <Button
            variant="icon"
            size="sm"
            icon="delete"
            aria-label={`Delete note ${note.name}`}
            title="Delete note"
            onClick={() => deleteNote(note.id)}
          />
        </CalendarActionToolbar>
      )}

      <div className="flex items-center gap-1 text-2xs font-bold text-slate-400 tabular-nums">
        {primaryTag && <TagIcon icon={primaryTag.icon} size={11} whiteTint />}
        <span className="truncate">{timeInfo.displayRange}</span>
        {timeInfo.nativeRange && (
          <span
            className="material-symbols-outlined text-xs leading-none text-sky-300"
            title={`Native time: ${timeInfo.nativeRange}`}
            aria-label={`Native time: ${timeInfo.nativeRange}`}
          >
            public
          </span>
        )}
        <span className="flex-1" />
        {locked && (
          <span
            className="material-symbols-outlined text-xs leading-none text-slate-400"
            title="Time locked"
          >
            lock
          </span>
        )}
      </div>

      {!compact && (
        <>
          {timeInfo.nativeRange && height >= 56 && (
            <div className="truncate text-2xs font-semibold text-sky-300">
              {timeInfo.nativeRange}
            </div>
          )}
          <div className="truncate text-xs font-bold leading-tight tracking-tight text-slate-50">
            {note.name}
          </div>
          {routeSubtitle && (
            <div className="truncate text-2xs font-semibold text-slate-400">
              {routeSubtitle}
            </div>
          )}
          {primaryTag && height >= 96 && (
            <span className="mt-0.5 self-start rounded-md bg-sky-900/60 px-1.5 py-px text-2xs font-bold text-sky-300">
              note · {primaryTag.name}
            </span>
          )}
        </>
      )}
      {compact && <span className="sr-only">{note.name}</span>}

      {geometry.clippedBottom && !resizing && (
        <span
          className="material-symbols-outlined absolute bottom-0 left-1/2 -translate-x-1/2 text-xs leading-none text-slate-400"
          title="Continues past midnight"
        >
          keyboard_double_arrow_down
        </span>
      )}
      {geometry.clippedTop && !resizing && (
        <span
          className="material-symbols-outlined absolute top-0 left-1/2 -translate-x-1/2 text-xs leading-none text-slate-400"
          title="Continues from the previous day"
        >
          keyboard_double_arrow_up
        </span>
      )}
    </div>
  );
}
